import { Injectable } from '@angular/core';
import {
    BiddingService,
    GetWatcherByIdResponse,
    WatchedItemsFlattenedListResponse,
    Response,
    WatcherType,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../state/app-state';
import { Observable, Subscriber } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { selectWatchlistPageState } from '../../modules/profiles/state/watchlist-page-state/watchlist-page-state.selectors';
import { WatchlistPageState } from '../../modules/profiles/state/watchlist-page-state/watchlist-page-state.reducer';
import { BiddingUtils } from '@/vb-utils/bidding.util';

/**
 * Enum for notification types.
 */
enum NotificationType {
    Success = 'success',
    Info = 'info',
    Error = 'error',
}

/**
 * Manages operations related to the user's watchlist.
 */
@Injectable({
    providedIn: 'root',
})
export class WatchlistService {
    /**
     * Constructs the WatchlistService with necessary dependencies.
     *
     * @param biddingService - Handles API interactions for bidding operations.
     * @param snackbarService - Displays notifications to the user.
     * @param translate - Handles translation of text.
     * @param store - Redux store for managing state.
     */
    constructor(
        private biddingService: BiddingService,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
        private store: Store<AppState>,
        private biddingUtils: BiddingUtils,
    ) {}

    /**
     * Adds a lot to the user's watchlist and emits the watchlist ID on success.
     * This method handles asynchronous operations and returns an Observable<string>.
     *
     * @param auctionId - The ID of the auction.
     * @param auctionLotId - The ID of the lot to be added.
     * @param title - The title of the lot, optional.
     * @returns An Observable<string> that emits the watchlist ID on success.
     */
    addToWatchlist(
        auctionId: string,
        auctionLotId: string,
        title?: string,
    ): Observable<string> {
        return new Observable<string>((subscriber: Subscriber<string>) => {
            // eslint-disable-next-line
            const watcherDetails = {
                auctionId: auctionId,
                auctionLotId: auctionLotId,
                watcherType: WatcherType.Watcher,
            };

            this.biddingService.createWatcher(watcherDetails).subscribe({
                next: (response: Response) => {
                    const watchlistId: string = response.data['id'];
                    subscriber.next(watchlistId);
                    subscriber.complete();
                    this.displayNotification(
                        NotificationType.Success,
                        'Watchlist.addToWatchlistSuccess',
                        title,
                    );
                    this.biddingUtils.updateWatchlist(
                        response.data as GetWatcherByIdResponse,
                        watchlistId,
                    );
                },
                error: () => {
                    subscriber.error(new Error('Failed to add to watchlist'));
                    this.displayNotification(
                        NotificationType.Error,
                        'Watchlist.addToWatchlistFailure',
                        title,
                    );
                },
            });
        });
    }

    /**
     * Removes a lot from the user's watchlist and resolves when the operation is complete.
     * This method handles the asynchronous operation using Promises.
     *
     * @param watchlistId - The ID of the watchlist to be removed.
     * @param title - The title of the lot, optional.
     * @returns A Promise that resolves when the operation is complete.
     */
    async removeFromWatchlist(
        watchlistId: string,
        title?: string,
    ): Promise<void> {
        try {
            await this.biddingService
                .deleteWatcherById(watchlistId)
                .toPromise();
            this.displayNotification(
                NotificationType.Info,
                'Watchlist.removeFromWatchlistSuccess',
                title,
            );
        } catch (error) {
            this.displayNotification(
                NotificationType.Error,
                'Watchlist.removeFromWatchlistFailure',
                title,
            );
        }
    }
    /**
     * Displays a notification based on the operation result.
     *
     * @param notificationType - Type of the notification indicating success or error.
     * @param messageKey - The key for the message to be translated.
     * @param title - The title of the item involved in the operation, optional.
     */
    private displayNotification(
        notificationType: NotificationType,
        messageKey: string,
        title?: string,
    ): void {
        // Retrieve the localized message using the message key and optional title
        const message: string = this.translate.instant(messageKey, { title });

        // Clear any existing messages
        this.snackbarService.dismissAll();

        // Display the appropriate notification message based on the notification type
        switch (notificationType) {
            case NotificationType.Success:
                this.snackbarService.success(message);
                break;
            case NotificationType.Error:
                this.snackbarService.error(message);
                break;
            default:
                this.snackbarService.system(message);
                break;
        }
    }

    /**
     * Retrieves the watchlist ID for a given auctionLotId.
     *
     * This method first checks if the watchlistPageState is available. If not, it dispatches an action
     * to load the watchlist. It then attempts to find the watchlist ID for the specified auctionLotId.
     *
     * @param auctionLotId - The ID of the lot to find the watchlist ID for.
     * @returns An Observable<string | undefined> containing the watchlist ID if found, otherwise undefined.
     */
    getWatchlistIdFromAuctionLotId(
        auctionLotId: string,
    ): Observable<string | undefined> {
        return this.store.select(selectWatchlistPageState).pipe(
            filter(
                (
                    watchlistPageState: WatchlistPageState | undefined,
                ): watchlistPageState is WatchlistPageState =>
                    watchlistPageState !== undefined,
            ),
            map((watchlistPageState: WatchlistPageState) => {
                // eslint-disable-next-line
                const watchlist = watchlistPageState.watchlist?.data?.find(
                    (watchlist: WatchedItemsFlattenedListResponse) =>
                        watchlist.auctionLotId === auctionLotId,
                );
                return watchlist?.lotId ?? undefined;
            }),
        );
    }

    /**
     * Retrieves the watchlist ID for a given lotId.
     *
     * This method first checks if the watchlistPageState is available. If not, it dispatches an action
     * to load the watchlist. It then attempts to find the watchlist ID for the specified lotId.
     *
     * @param lotId - The ID of the lot to find the watchlist ID for.
     * @returns An Observable<string | undefined> containing the watchlist ID if found, otherwise undefined.
     */
    getWatchlistIdFromLotId(lotId: string): Observable<string | undefined> {
        return this.store.select(selectWatchlistPageState).pipe(
            filter(
                (
                    watchlistPageState: WatchlistPageState | undefined,
                ): watchlistPageState is WatchlistPageState =>
                    watchlistPageState !== undefined,
            ),
            map((watchlistPageState: WatchlistPageState) => {
                // eslint-disable-next-line
                const watchlist = watchlistPageState.watchlist?.data?.find(
                    (watchlist: WatchedItemsFlattenedListResponse) =>
                        watchlist.lotId === lotId,
                );
                return watchlist?.lotId ?? undefined;
            }),
        );
    }
}
